import React from 'react';
import {
  BanknotesIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  Cog6ToothIcon,
  DocumentChartBarIcon,
} from '@heroicons/react/24/outline';

type PlanCard = {
  title: string;
  description: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
};

const ManageSubscriptionsPage: React.FC = () => {
  const readinessItems = [
    'Tenant plan and billing profile',
    'Default currency from organisation settings',
    'Enabled module count and usage limits',
    'Invoice contact and tax details',
    'Payment provider integration status',
  ];
  const planCards: PlanCard[] = [
    { title: 'Starter', description: 'Core farmer, crop, and activity workflows', Icon: BuildingOffice2Icon },
    { title: 'Traceability', description: 'Tree passport, QR, reports, and mobile scouting', Icon: DocumentChartBarIcon },
    { title: 'Enterprise', description: 'Custom modules, marketplace, integrations, support', Icon: Cog6ToothIcon },
  ];

  return (
    <div className="min-h-screen bg-slate-100 p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <section className="overflow-hidden rounded-3xl bg-slate-950 text-white shadow-xl">
          <div className="grid gap-8 p-8 lg:grid-cols-[1.1fr_0.9fr] lg:p-10">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.24em] text-emerald-300">Platform Owner Console</p>
              <h1 className="mt-4 text-4xl font-black tracking-tight">Manage Subscriptions</h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300">
                Prepare organisation billing, module packages, currencies, and invoice readiness from one place. Payment automation can plug into this structure later.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {[
                ['Plans', '3 draft tiers'],
                ['Currencies', 'Tenant default'],
                ['Invoices', 'Ready for setup'],
                ['Payments', 'Manual / provider-ready'],
              ].map(([label, value]) => (
                <div key={label} className="rounded-2xl border border-white/10 bg-white/10 p-4">
                  <p className="text-xs font-black uppercase tracking-wider text-slate-400">{label}</p>
                  <p className="mt-2 text-xl font-black">{value}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="grid gap-5 lg:grid-cols-[0.8fr_1.2fr]">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-emerald-50 p-3 text-emerald-700">
                <BanknotesIcon className="h-6 w-6" />
              </div>
              <div>
                <h2 className="font-black text-slate-950">Billing Readiness</h2>
                <p className="text-sm text-slate-500">Operational checklist before payment automation.</p>
              </div>
            </div>
            <div className="mt-5 space-y-3">
              {readinessItems.map((item) => (
                <div key={item} className="flex items-center gap-3 rounded-2xl bg-slate-50 p-3 text-sm font-bold text-slate-700">
                  <CheckCircleIcon className="h-5 w-5 text-emerald-600" />
                  {item}
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="font-black text-slate-950">Subscription Model</h2>
                <p className="text-sm text-slate-500">Use Manage Organisations to assign modules now. Billing automation comes next.</p>
              </div>
              <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-black uppercase tracking-wider text-amber-700">Provider-ready</span>
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-3">
              {planCards.map(({ title, description, Icon }) => (
                <div key={title} className="rounded-2xl border border-slate-200 p-5">
                  <Icon className="h-7 w-7 text-emerald-700" />
                  <h3 className="mt-4 font-black text-slate-950">{title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-500">{description}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5 text-sm leading-6 text-slate-600">
              No live charging is performed on this page yet. It is the governance surface for subscription planning, tenant package review, and future payment-provider integration.
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ManageSubscriptionsPage;
